/**
 * I passi del tutorial guidato dell'elenco pubblicazioni Amazon.
 *
 * Il giro passa da Scarica/Carica in alto e dalle schede sotto il titolo, che
 * lavorano su tutte le pubblicazioni, poi dal menu a tre pallini a inizio riga,
 * che agisce su una sola: quello va aperto per illuminarne le voci, ed e' quello
 * che fanno i `before`; `stopAmazonListTourMenus` lo richiude a fine giro.
 *
 * I passi che dipendono dai dati - la tabella e le voci di riga - hanno
 * `timeout: 250`: a elenco vuoto il bersaglio non c'e' e la card si aggancia in
 * basso invece di far aspettare.
 */

/** Bersaglio di riferimento per capire se un menu e' davvero a schermo. */
const ROW_PROBE = '[data-tour="row-sync"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}
/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva `click-outside` lo ha registrato in cattura - cioe' **prima** che il
 * pulsante eseguisse il proprio handler - e ha accodato la chiusura con un
 * `setTimeout(0)`. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 * Il nostro rinvio e' accodato per ultimo, quindi arriva quando la chiusura e'
 * gia' avvenuta.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il menu abbia finito di aprirsi o chiudersi.
 *
 * L'attesa sta qui e non nel `timeout` del passo, ed e' una differenza che conta:
 * `before` viene eseguito prima che il motore si metta a cercare il bersaglio, e
 * qui si aspetta **solo quando c'e' davvero un menu da aprire**. Cosi' i passi
 * possono tenere un `timeout` corto e, a elenco vuoto, agganciarsi subito in basso
 * invece di far aspettare a vuoto.
 */
async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	// Niente attivatore - elenco vuoto - non c'e' niente da aspettare.
	if (!element) {
		return;
	}

	element.click();
	await waitFor(probe, true);
}

async function closeMenu(activator, probe) {
	await afterPendingClose();

	if (!isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	// L'attivatore fa da interruttore: il secondo click richiude.
	if (element) {
		element.click();
		await waitFor(probe, false);
	}
}

// Sempre la prima riga: `querySelector` prende quella, ed e' l'unica che serve.
const openRow = () => openMenu('[data-tour="row-menu"]', ROW_PROBE);

/** Richiude tutto: vale a fine giro e sui passi che devono trovare la pagina pulita. */
export async function stopAmazonListTourMenus() {
	await closeMenu('[data-tour="row-menu"]', ROW_PROBE);
}

export function createAmazonListTourSteps() {
	return [
		{
			body: "Qui c'e' l'elenco di quello che hai pubblicato su Amazon: una riga per ogni prodotto su ogni marketplace, quindi lo stesso articolo compare piu' volte se lo vendi in piu' paesi. In alto trovi Scarica catalogo e Carica modifiche, che lavorano su tutte le pubblicazioni insieme, e sotto il titolo le schede delle altre pagine della sezione; i tre pallini a inizio riga invece agiscono su una sola.",
			key: 'intro',
			placement: 'bottom',
			target: '[data-tour="tutorial"]',
			title: 'Le pubblicazioni su Amazon',
		},
		{
			body: "La ricerca parte da tre caratteri: sotto quella soglia il filtro si azzera e torna l'elenco intero. Marketplace restringe a un paese. Status distingue le tre situazioni in cui puo' trovarsi una pubblicazione: Confirmed e' stata accettata da Amazon ed e' l'unica su cui funziona la modifica diretta; Pending e' partita e si aspetta ancora la risposta; Void e' quella chiusa, perche' Amazon l'ha rifiutata o perche' non ha mai risposto.",
			key: 'filters',
			placement: 'bottom',
			target: '[data-tour="filters"]',
			title: 'Cercare e filtrare',
		},
		{
			body: "La colonna Prodotto arriva dal tuo catalogo - nome, sku, ean, giacenza, prezzo - la colonna Amazon dall'inserzione: il sito, il link che la apre, l'Asin e il Seller Sku. Nell'ultima colonna, Prezzi Amazon, prezzo, valuta, quantita' e spedizione si modificano direttamente qui: appena cambi un valore compare un'icona di salvataggio accanto al campo, e finche' non la premi non parte niente. Il salvataggio mette l'operazione in coda, non scrive su Amazon all'istante.",
			key: 'table',
			placement: 'top',
			target: '[data-tour="table"]',
			timeout: 250,
			title: 'Come si legge una riga',
		},
		{
			body: "Non riguarda la riga su cui sei: lavora su tutte le pubblicazioni di un marketplace. Chiede il marketplace e il formato, Excel o CSV. Nell'elenco dei marketplace compaiono solo quelli dove hai davvero pubblicazioni, col numero accanto, cosi' non si sceglie a vuoto. Il file nasce dal report ufficiale di Amazon e puo' metterci qualche minuto: non devi restare ad aspettare, ti arrivano una mail e una notifica con il link per scaricarlo.",
			key: 'catalog-download',
			placement: 'bottom',
			target: '[data-tour="catalog-download"]',
			title: 'Scarica catalogo',
		},
		{
			body: "E' lo stesso file di prima, ricaricato dopo averlo modificato. Si leggono solo tre colonne - NuovoPrezzo, NuovaQt, NuovoStatus - e le righe lasciate vuote non vengono toccate. Prima di applicare passi sempre dall'anteprima, che conta quanti prezzi, quante giacenze, quante chiusure e quante righe sono state scartate e perche': a quel punto non e' stato ancora scritto niente. Nell'anteprima togli la spunta alle righe che non vuoi, poi Applica manda tutte le altre insieme: vanno dirette ad Amazon, e il catalogo si aggiorna solo per quelle che Amazon accetta; alla fine una mail ti elenca anche quelle rifiutate. Due limiti da tenere a mente: NuovoStatus puo' solo chiudere una pubblicazione, per riaprirla va ripubblicato il prodotto; e la giacenza forzata dal file vale fino al primo ricalcolo dal magazzino.",
			key: 'catalog-import',
			placement: 'bottom',
			target: '[data-tour="catalog-import"]',
			title: 'Carica modifiche',
		},
		{
			body: "E' una scheda a se', perche' ci si lavora a lungo: l'elenco delle inserzioni che stanno su Amazon ma non risultano collegate a nessun prodotto del tuo catalogo - tipicamente roba pubblicata prima, o da un altro strumento. Si sceglie il paese e si preme Aggiorna elenco per farsi leggere il report di Amazon, poi si associa una riga per volta. Le inserzioni senza SellerSku non sono agganciabili da li'.",
			key: 'catalog-unmatched',
			placement: 'bottom',
			target: '[data-tour="tab-unmatched"]',
			title: 'Associa prodotti',
		},
		{
			body: "Anche questa e' una scheda: tutte le tue pubblicazioni attive, associate o no, dove segui con una stellina i prodotti che ti interessano. Effettua analisi legge da Amazon, solo per quelli seguiti, chi ha la buybox e a che prezzo, e la tua offerta con la spedizione: il confronto e' fra i totali che paga il cliente. Non e' immediata - va in coda, ti dice prima quanto ci mettera' e ti avvisa alla fine - e da li' Aggiusta prezzo manda subito ad Amazon il nuovo prezzo, lo stesso invio di Modifica su cms/marketplace. L'elenco si aggiorna insieme ad Associa prodotti, con un solo download.",
			key: 'catalog-price-analysis',
			placement: 'bottom',
			target: '[data-tour="tab-price-analysis"]',
			title: 'Analisi prezzi',
		},
		{
			body: "Le ultime due schede lavorano da sole, senza che tu debba toccare le righe una per una. Regole feed cambia prezzo e quantita' che mandiamo a Amazon quando un prodotto rispetta delle condizioni - per esempio piu' 5% su una marca, o quantita' a zero sotto i due pezzi - e ogni regola si accende e si spegne con un interruttore. Repricing insegue i concorrenti dentro un prezzo minimo e uno massimo che decidi tu.",
			key: 'tab-rules',
			placement: 'bottom',
			target: '[data-tour="tab-rules"]',
			title: 'Regole feed e Repricing',
		},
		{
			body: "Passiamo all'altro menu. I tre pallini a inizio riga aprono quattro voci che valgono solo per quella pubblicazione. Due delle quattro cancellano qualcosa, e non la stessa cosa: vale la pena sapere esattamente quale fa cosa prima di usarle.",
			before: stopAmazonListTourMenus,
			key: 'row-menu',
			placement: 'right',
			target: '[data-tour="row-menu"]',
			timeout: 250,
			title: 'Il menu della singola riga',
		},
		{
			body: "Dopo una conferma, toglie il collegamento fra il prodotto del tuo catalogo e la pubblicazione: la riga sparisce da questo elenco e smetti di gestire quell'inserzione da qui. Su Amazon l'inserzione resta al suo posto e continua a vendere. Si usa quando il collegamento e' sbagliato, o quando vuoi che quel prodotto non venga piu' aggiornato in automatico.",
			before: openRow,
			key: 'row-sync',
			placement: 'right',
			target: '[data-tour="row-sync"]',
			timeout: 250,
			title: 'Elimina sincronizzazione',
		},
		{
			body: "Questa invece tocca davvero Amazon, ed e' l'unica che lo fa. Chiede conferma, poi chiede ad Amazon di eliminare l'inserzione: solo se Amazon accetta la riga viene tolta anche da qui. Se Amazon rifiuta, i motivi compaiono a schermo e non si perde niente. Non confonderla con la voce sopra: quella scollega, questa cancella.",
			before: openRow,
			key: 'row-delete',
			placement: 'right',
			target: '[data-tour="row-delete"]',
			timeout: 250,
			title: 'Elimina da cms/marketplace',
		},
		{
			body: "Compare solo sulle righe in stato Confirmed, perche' e' l'unico caso in cui Amazon accetta una modifica diretta. Manda subito prezzo, quantita' e valuta cosi' come li vedi scritti in quella riga, senza passare dalla coda: e' la scorciatoia per correggere una cosa sola in fretta. Se Amazon rifiuta ti elenca i motivi; se accetta, la riga si ricarica con i valori nuovi.",
			before: openRow,
			key: 'row-edit',
			placement: 'right',
			target: '[data-tour="row-edit"]',
			timeout: 250,
			title: 'Modifica su cms/marketplace',
		},
		{
			body: "L'ultima voce non tocca niente: apre la scheda completa del prodotto del catalogo, la stessa che trovi nella sezione Prodotti. Serve per andare a vedere o correggere il prodotto - titolo, immagini, giacenza reale - non la pubblicazione su Amazon.",
			before: openRow,
			key: 'row-detail',
			placement: 'right',
			target: '[data-tour="row-detail"]',
			timeout: 250,
			title: 'Visualizza dettaglio prodotto',
		},
	];
}
